import type { JsonMessage } from './transport';
import { asText, randomId } from './transport';

/**
 * Direct player-to-player trade (research 04 §5.5). Both sides edit an offer,
 * lock it, then confirm; a confirmation names the exact offer versions it
 * agrees to, so an offer changed after locking can never be committed. When a
 * side holds both confirmations it applies the exchange locally.
 *
 * There is no server: each client moves its own items (the honest-client
 * model the rest of the game uses). Only stackable, unbound goods and silver
 * can be traded.
 */

export const TRADE_MAX_LINES = 8;
export const TRADE_MAX_SILVER = 10_000_000;
const INVITE_MS = 30000;

export interface TradeLine {
  id: string;
  count: number;
}

export interface Offer {
  items: TradeLine[];
  silver: number;
}

export interface TradeInventory {
  count(id: string): number;
  tradable(id: string): boolean;
  take(id: string, n: number): boolean;
  give(id: string, n: number): number;
  silver(): number;
  takeSilver(n: number): boolean;
  giveSilver(n: number): void;
  /** Free bag slots for new stacks. */
  freeSlots(): number;
}

export interface TradeDeps {
  selfId(): string;
  selfName(): string;
  now(): number;
  /** Sends through a room shared with `to`; false if unreachable. */
  send(msg: JsonMessage, to: string): boolean;
  inventory: TradeInventory;
  changed(): void;
  notify(text: string): void;
  /** Incoming trade request (UI shows accept/decline). */
  invited(tid: string, from: string, name: string): void;
}

export interface TradeSession {
  tid: string;
  peer: string;
  peerName: string;
  mine: Offer;
  theirs: Offer;
  /** Offer versions (bumped on every edit). */
  v: number;
  tv: number;
  myLock: boolean;
  theirLock: boolean;
  myOk: boolean;
  theirOk: boolean;
}

const emptyOffer = (): Offer => ({ items: [], silver: 0 });

function sanitizeOffer(o: unknown, tradable: (id: string) => boolean): Offer | null {
  if (!o || typeof o !== 'object') return null;
  const raw = o as { items?: unknown; silver?: unknown };
  const silver = Math.floor(Number(raw.silver) || 0);
  if (silver < 0 || silver > TRADE_MAX_SILVER || !Array.isArray(raw.items) || raw.items.length > TRADE_MAX_LINES) return null;
  const items: TradeLine[] = [];
  for (const l of raw.items) {
    const id = (l as TradeLine | undefined)?.id;
    const count = Math.floor(Number((l as TradeLine | undefined)?.count) || 0);
    if (typeof id !== 'string' || !tradable(id) || count <= 0 || count > 9999) return null;
    items.push({ id, count });
  }
  return { items, silver };
}

export class TradeManager {
  session: TradeSession | null = null;
  /** Outgoing request waiting for an answer. */
  private outgoing: { tid: string; peer: string; name: string; until: number } | null = null;
  /** Incoming requests we may still accept. */
  private incoming = new Map<string, { peer: string; name: string; until: number }>();

  constructor(private d: TradeDeps) {}

  request(peer: string, name: string): boolean {
    if (this.session) {
      this.d.notify('Finish your current trade first.');
      return false;
    }
    const tid = randomId(10);
    if (!this.d.send({ t: 'tinv', tid, name: this.d.selfName() }, peer)) {
      this.d.notify(`${name} is out of reach.`);
      return false;
    }
    this.outgoing = { tid, peer, name, until: this.d.now() + INVITE_MS };
    this.d.notify(`Trade request sent to ${name}.`);
    return true;
  }

  accept(tid: string): void {
    const inv = this.incoming.get(tid);
    this.incoming.delete(tid);
    if (!inv || this.session || this.d.now() > inv.until) return;
    if (!this.d.send({ t: 'tacc', tid, name: this.d.selfName() }, inv.peer)) return;
    this.open(tid, inv.peer, inv.name);
  }

  decline(tid: string): void {
    const inv = this.incoming.get(tid);
    this.incoming.delete(tid);
    if (inv) this.d.send({ t: 'tno', tid }, inv.peer);
  }

  private open(tid: string, peer: string, peerName: string): void {
    this.session = { tid, peer, peerName, mine: emptyOffer(), theirs: emptyOffer(), v: 0, tv: 0, myLock: false, theirLock: false, myOk: false, theirOk: false };
    this.outgoing = null;
    this.d.changed();
  }

  /** Replaces our offer (only while unlocked); returns an error message on failure. */
  setOffer(offer: Offer): string | null {
    const s = this.session;
    if (!s) return 'No trade open.';
    if (s.myLock) return 'Unlock your offer to change it.';
    const inv = this.d.inventory;
    const clean = sanitizeOffer(offer, (id) => inv.tradable(id));
    if (!clean) return 'That offer is not allowed.';
    for (const l of clean.items) if (inv.count(l.id) < l.count) return 'You do not have enough of that.';
    if (clean.silver > inv.silver()) return 'Not enough silver.';
    s.mine = clean;
    s.v++;
    s.theirLock = false;
    s.myOk = s.theirOk = false;
    this.d.send({ t: 'toffer', tid: s.tid, v: s.v, offer: clean }, s.peer);
    this.d.changed();
    return null;
  }

  lock(on = true): void {
    const s = this.session;
    if (!s || s.myLock === on) return;
    s.myLock = on;
    s.myOk = s.theirOk = false;
    this.d.send({ t: 'tlock', tid: s.tid, v: s.v, on }, s.peer);
    this.d.changed();
  }

  confirm(): void {
    const s = this.session;
    if (!s || !s.myLock || !s.theirLock || s.myOk) return;
    s.myOk = true;
    this.d.send({ t: 'tok', tid: s.tid, mine: s.v, theirs: s.tv }, s.peer);
    this.d.changed();
    if (s.theirOk) this.commit();
  }

  cancel(reason?: string): void {
    const s = this.session;
    if (!s) return;
    this.d.send({ t: 'tx', tid: s.tid }, s.peer);
    this.session = null;
    if (reason) this.d.notify(reason);
    this.d.changed();
  }

  /** The partner disconnected or left the zone. */
  peerGone(peer: string): void {
    if (this.session?.peer === peer) {
      this.session = null;
      this.d.notify('Trade cancelled: your partner left.');
      this.d.changed();
    }
  }

  private commit(): void {
    const s = this.session;
    if (!s) return;
    this.session = null;
    const inv = this.d.inventory;
    // Re-check our side right before moving anything.
    const ok = s.mine.items.every((l) => inv.count(l.id) >= l.count) && inv.silver() >= s.mine.silver;
    const newStacks = s.theirs.items.filter((l) => inv.count(l.id) === 0).length;
    if (!ok || newStacks > inv.freeSlots() + s.mine.items.filter((l) => inv.count(l.id) === l.count).length) {
      this.d.send({ t: 'tx', tid: s.tid }, s.peer);
      this.d.notify('Trade failed: your bag changed or is full.');
      this.d.changed();
      return;
    }
    for (const l of s.mine.items) inv.take(l.id, l.count);
    if (s.mine.silver) inv.takeSilver(s.mine.silver);
    for (const l of s.theirs.items) inv.give(l.id, l.count);
    if (s.theirs.silver) inv.giveSilver(s.theirs.silver);
    this.d.notify(`Trade with ${s.peerName} complete.`);
    this.d.changed();
  }

  onMessage(from: string, m: JsonMessage): void {
    const now = this.d.now();
    const s = this.session;
    switch (m.t) {
      case 'tinv': {
        if (typeof m.tid !== 'string') return;
        const name = asText(m.name, '?', 24);
        if (this.session) {
          this.d.send({ t: 'tbusy', tid: m.tid }, from);
          return;
        }
        this.incoming.set(m.tid, { peer: from, name, until: now + INVITE_MS });
        this.d.invited(m.tid, from, name);
        return;
      }
      case 'tacc': {
        const o = this.outgoing;
        if (!o || o.tid !== m.tid || o.peer !== from || now > o.until || this.session) return;
        this.open(o.tid, from, o.name);
        return;
      }
      case 'tno':
      case 'tbusy': {
        const o = this.outgoing;
        if (o && o.tid === m.tid && o.peer === from) {
          this.outgoing = null;
          this.d.notify(m.t === 'tbusy' ? `${o.name} is busy trading.` : `${o.name} declined the trade.`);
        }
        return;
      }
      default:
        break;
    }
    if (!s || s.tid !== m.tid || s.peer !== from) return;
    switch (m.t) {
      case 'toffer': {
        const offer = sanitizeOffer(m.offer, (id) => this.d.inventory.tradable(id));
        const v = Number(m.v);
        if (!offer || !(v > s.tv)) {
          this.cancel('Trade cancelled: invalid offer.');
          return;
        }
        s.theirs = offer;
        s.tv = v;
        // Any change unlocks our side too, so nobody confirms a stale view.
        s.myLock = false;
        s.theirLock = false;
        s.myOk = s.theirOk = false;
        this.d.changed();
        return;
      }
      case 'tlock':
        if (Number(m.v) !== s.tv) return;
        s.theirLock = m.on !== false;
        s.myOk = s.theirOk = false;
        this.d.changed();
        return;
      case 'tok':
        // Their confirmation must match both offers exactly as we see them.
        if (Number(m.mine) !== s.tv || Number(m.theirs) !== s.v || !s.theirLock) return;
        s.theirOk = true;
        this.d.changed();
        if (s.myOk) this.commit();
        return;
      case 'tx':
        this.session = null;
        this.d.notify(`${s.peerName} cancelled the trade.`);
        this.d.changed();
        return;
      default:
        return;
    }
  }

  tick(): void {
    const now = this.d.now();
    if (this.outgoing && now > this.outgoing.until) {
      this.d.notify(`${this.outgoing.name} did not answer the trade request.`);
      this.outgoing = null;
    }
    for (const [tid, inv] of this.incoming) if (now > inv.until) this.incoming.delete(tid);
  }
}
